// useRevealOnScroll — one-shot scroll-reveal for list items.
//
// Returns a ref for a container. While active, it watches every descendant
// carrying `data-sd-reveal` and adds the `is-revealed` class the first time each
// enters the viewport, then stops watching it (one-shot — items don't re-hide on
// scroll-up). The actual motion is pure CSS, gated by the root `.sd--anim` class
// and honouring prefers-reduced-motion; this hook only toggles the class, so it's
// a harmless no-op when animations are off (CSS leaves un-revealed items visible).
//
// A MutationObserver re-scans when the list changes (catalog rows re-render on
// series/search changes), so newly-added rows get observed too. Items already in
// view at mount reveal immediately (IntersectionObserver fires for them on
// observe), which — with the CSS stagger — gives the initial cascade.

import { useEffect, useRef } from "react";

export function useRevealOnScroll<T extends HTMLElement>(active: boolean) {
  const containerRef = useRef<T | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // No IntersectionObserver (old embed host / SSR) → reveal everything up-front
    // so nothing can get stuck hidden under the CSS start state.
    if (!active || typeof IntersectionObserver === "undefined") {
      container.querySelectorAll<HTMLElement>("[data-sd-reveal]").forEach(el =>
        el.classList.add("is-revealed"),
      );
      return;
    }

    const io = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target); // one-shot
          }
        }
      },
      // Reveal a touch before fully in view so items aren't caught mid-scroll.
      { threshold: 0.05, rootMargin: "0px 0px -8% 0px" },
    );

    const observeNew = () => {
      container
        .querySelectorAll<HTMLElement>("[data-sd-reveal]:not(.is-revealed)")
        .forEach(el => io.observe(el));
    };
    observeNew();

    // Re-observe rows added on later renders (search, series switch, load-more).
    const mo = new MutationObserver(observeNew);
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [active]);

  return containerRef;
}
